// filepath: /Users/ephraim/yesodcopilot/client/src/components/group/MembershipCard.js
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faCheck,
  faEllipsisV, 
  faEdit, 
  faTrash, 
  faShare, 
  faCopy, 
  faTimes,
  faExclamationTriangle,
  faIdCard,
  faUserCheck 
} from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../../context/AuthContext';
import { copyToClipboardEnhanced } from '../../utils/linkUtils';
import '../../styles/group/EventCard.css'; // Reusing event card styles for consistency
import '../../styles/group/MembershipCard.css'; // Our custom membership card styles

const MembershipCard = ({ membership, groupId, isAdmin, onEditMembership, onDeleteMembership }) => {
  const [imageError, setImageError] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [copySuccess, setCopySuccess] = useState(false);
  const menuRef = useRef(null);
  const modalRef = useRef(null);
  const deleteModalRef = useRef(null);
  const shareUrlRef = useRef(null);
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  
  // Process image URL to handle special characters in Supabase URLs
  const processImageUrl = (url) => {
    if (!url) return null;
    
    // Check if the url is not a string (e.g., it's an object)
    if (typeof url !== 'string') {
      console.error('Invalid URL type:', typeof url, url);
      return null;
    }
    
    try {
      console.log('Original image URL:', url);
      
      // First try to create a valid URL object to see if it's parseable
      try {
        new URL(url);
      } catch (e) {
        console.error('Invalid URL format:', url);
        return null;
      }
      
      // Extract the base domain and path
      const urlObj = new URL(url);
      const baseDomain = `${urlObj.protocol}//${urlObj.host}`;
      const pathParts = urlObj.pathname.split('/');
      
      // Process each part of the path to properly encode
      const processedPathParts = pathParts.map((part, index) => {
        // Don't encode the first empty part (from the leading slash)
        if (index === 0 && part === '') return '';
        
        // For the filename (last part), ensure it's properly encoded
        if (index === pathParts.length - 1) {
          try {
            // First try to decode it in case it's already encoded but with issues
            const decodedPart = decodeURIComponent(part);
            // Then properly encode it
            return encodeURIComponent(decodedPart);
          } catch (e) {
            // If decoding fails, just encode as is
            return encodeURIComponent(part);
          }
        }
        
        // For path segments, just ensure they're properly encoded
        return encodeURIComponent(part);
      });
      
      // Build the URL back
      const processedPath = processedPathParts.join('/');
      let processedUrl = `${baseDomain}${processedPath}`;
      
      // Add query parameters if they exist
      if (urlObj.search) {
        processedUrl += urlObj.search;
      }
      
      console.log('Processed image URL:', processedUrl);
      
      // Add a cache buster to prevent browser caching issues
      const separator = processedUrl.includes('?') ? '&' : '?';
      return `${processedUrl}${separator}t=${Date.now()}`;
    } catch (error) {
      console.error('Error processing image URL:', error);
      return null;
    }
  };
  
  // Generate a URL for the membership
  const generateMembershipUrl = () => {
    return `${window.location.origin}/group/${groupId}/memberships/${membership.id}`;
  };

  // Handle membership card click - navigate to membership details page
  const handleCardClick = () => {
    navigate(`/group/${groupId}/memberships/${membership.id}`);
  };

  // Toggle the action menu
  const toggleMenu = (e) => {
    e.stopPropagation();
    setShowMenu(!showMenu);
  };

  // Handle edit action
  const handleEdit = (e) => {
    e.stopPropagation();
    setShowMenu(false);
    
    if (onEditMembership) {
      onEditMembership(membership);
    }
  };

  // Open delete confirmation modal
  const openDeleteModal = (e) => {
    e.stopPropagation();
    setShowMenu(false);
    setShowDeleteModal(true);
  };

  // Close delete confirmation modal
  const closeDeleteModal = (e) => {
    if (e) e.stopPropagation();
    setShowDeleteModal(false);
  };

  // Confirm deletion
  const confirmDelete = (e) => {
    e.stopPropagation();
    if (onDeleteMembership) {
      onDeleteMembership(membership.id);
    }
    setShowDeleteModal(false);
  };

  // Handle share action
  const handleShare = (e) => {
    e.stopPropagation();
    setShowMenu(false);
    setShowShareModal(true);
    setCopySuccess(false);
  };

  // Handle copy to clipboard
  const handleCopyToClipboard = async (e) => {
    e.stopPropagation();
    
    const membershipUrl = generateMembershipUrl();
    
    try {
      // Use the browser's clipboard API directly to avoid modal issues
      await navigator.clipboard.writeText(membershipUrl);
      
      // Update local state to show success feedback
      setCopySuccess(true);
      
      // Reset the success state after 2 seconds
      setTimeout(() => {
        setCopySuccess(false);
      }, 2000);
      
    } catch (err) {
      // Fallback for older browsers
      try {
        const textArea = document.createElement('textarea');
        textArea.value = membershipUrl;
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        textArea.style.top = '-999999px';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        
        const successful = document.execCommand('copy');
        document.body.removeChild(textArea);
        
        if (successful) {
          setCopySuccess(true);
          setTimeout(() => {
            setCopySuccess(false);
          }, 2000);
        } else {
          console.error('Failed to copy membership link');
        }
      } catch (fallbackErr) {
        console.error('Copy to clipboard failed:', fallbackErr);
      }
    }
  };

  // Close share modal
  const handleCloseShareModal = (e) => {
    if (e) e.stopPropagation();
    setShowShareModal(false);
  };

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setShowMenu(false);
      }
      
      if (showShareModal && modalRef.current && !modalRef.current.contains(event.target)) {
        setShowShareModal(false);
      }

      if (showDeleteModal && deleteModalRef.current && !deleteModalRef.current.contains(event.target)) {
        setShowDeleteModal(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showShareModal, showDeleteModal]);

  // Enhanced error handling for the image
  const handleImageError = () => {
    console.error(`Failed to load image for membership ${membership.id || 'unknown'}:`, {
      originalUrl: membership.cover_image_url,
      processedUrl: processImageUrl(membership.cover_image_url),
      membershipData: {
        id: membership.id,
        name: membership.name
      }
    });
    
    setImageError(true);
  };

  useEffect(() => {
    // Reset the error state when the membership changes
    setImageError(false);
    
    // Set imageError to true immediately if there's no valid cover image URL
    if (!membership.cover_image_url) {
      console.log(`Membership ${membership.id} has no cover_image_url, using placeholder`);
      setImageError(true);
    } else {
      console.log(`Membership ${membership.id} has cover_image_url:`, membership.cover_image_url);
    }
  }, [membership, membership.cover_image_url]);

  // Handle join button click
  const handleJoin = (e) => {
    e.stopPropagation();
    console.log(`Join membership: ${membership.name}`);
    // Add functionality to subscribe to the membership
  };

  // Determine button text based on membership type
  const getButtonText = () => {
    // If recurring is false and interval is null or 'one-time', show "Support"
    if (membership.recurring === false || membership.interval === 'one-time' || membership.interval === null) {
      return 'Support';
    }
    // Otherwise show "Join" for recurring memberships
    return 'Join';
  };

  // Process benefits array
  const membershipBenefits = Array.isArray(membership.benefits) 
    ? membership.benefits 
    : typeof membership.benefits === 'string'
      ? JSON.parse(membership.benefits)
      : [];

  return (
    <div className="event-card membership-card" onClick={handleCardClick}>
      {/* Cover image section */}
      <div className="event-card-image-container">
        {!imageError ? (
          <img 
            className="event-card-img" 
            src={processImageUrl(membership.cover_image_url)}
            alt={membership.name}
            onError={handleImageError}
          />
        ) : (
          <div className="event-card-placeholder">
            <FontAwesomeIcon icon={faIdCard} />
          </div>
        )}
        
        {/* Admin actions menu (three dots) */}
        {isAdmin && (
          <div className="event-action-menu-container" ref={menuRef}>
            <button className="event-action-menu-button" onClick={toggleMenu}>
              <FontAwesomeIcon icon={faEllipsisV} />
            </button>
            
            {showMenu && (
              <div className="event-action-menu">
                <button className="event-action-item" onClick={handleShare}>
                  <FontAwesomeIcon icon={faShare} /> Share
                </button>
                <button className="event-action-item" onClick={handleEdit}>
                  <FontAwesomeIcon icon={faEdit} /> Edit
                </button>
                <button className="event-action-item delete" onClick={openDeleteModal}>
                  <FontAwesomeIcon icon={faTrash} /> Delete
                </button>
              </div>
            )}
          </div>
        )}
      </div>
      
      {/* Content section with clean white background */}
      <div className="event-card-content">
        {/* Membership title */}
        <h3 className="event-card-title">{membership.name}</h3>
        
        {/* Price with interval */}
        <div className="membership-price">
          ${membership.price}
          {membership.interval === 'one-time' ? (
            // For one-time payments, show nothing after the price
            null
          ) : membership.recurring && (
            <span className="recurring-label">
              /{membership.interval}
            </span>
          )}
        </div>
        
        {/* Membership description */}
        <p className="event-card-description">
          {membership.description && membership.description.length > 100 
            ? `${membership.description.substring(0, 100)}...` 
            : membership.description}
        </p>
        
        {/* Benefits/Features */}
        {membershipBenefits && membershipBenefits.length > 0 && (
          <ul className="membership-benefits">
            {membershipBenefits.map((benefit, index) => (
              <li key={index}>
                <FontAwesomeIcon icon={faCheck} className="benefit-check" />
                {benefit}
              </li>
            ))}
          </ul>
        )}
        
        {/* Join button */}
        <div className="event-card-actions">
          <button 
            className="join-button" 
            onClick={handleJoin}
          >
            {getButtonText()}
          </button>
        </div>
      </div>
      
      {/* Share modal */}
      {showShareModal && (
        <div className="share-modal-overlay" onClick={handleCloseShareModal}>
          <div className="share-modal" ref={modalRef} onClick={(e) => e.stopPropagation()}>
            <div className="share-modal-header">
              <h3>Share Membership</h3>
              <button className="close-modal-btn" onClick={handleCloseShareModal}>
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>
            <div className="share-modal-content">
              <p>Copy this link to share the membership:</p>
              <div className="share-url-container">
                <input 
                  type="text" 
                  ref={shareUrlRef}
                  className="share-url-input" 
                  value={generateMembershipUrl()} 
                  readOnly 
                  onClick={(e) => e.target.select()} 
                />
                <button 
                  className={`copy-button ${copySuccess ? 'success' : ''}`} 
                  onClick={handleCopyToClipboard}
                >
                  <FontAwesomeIcon icon={faCopy} />
                  {copySuccess ? 'Copied!' : 'Copy to Clipboard'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="delete-modal-overlay" onClick={closeDeleteModal}>
          <div className="delete-modal" ref={deleteModalRef} onClick={(e) => e.stopPropagation()}>
            <div className="delete-modal-header">
              <FontAwesomeIcon icon={faExclamationTriangle} className="warning-icon" />
              <h3>Delete Membership</h3>
            </div>
            <div className="delete-modal-content">
              <p>Are you sure you want to delete "{membership.name}"?</p>
              <p className="delete-warning">This action cannot be undone.</p>
              
              <div className="delete-modal-actions">
                <button className="btn-cancel" onClick={closeDeleteModal}>
                  Cancel
                </button>
                <button className="btn-delete" onClick={confirmDelete}>
                  Delete Membership
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MembershipCard;